import { Space, Tag } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, orDash } from '../../lib/format';
import { CUSTOMER_HISTORY, type CustomerJobCard } from './api';
import { HistoryCard } from './HistoryCard';
import { HistorySection } from './HistorySection';

/** "Model · plate", or the chassis when the bike has no plate yet. */
function bike(row: CustomerJobCard): string {
  return [row.model, row.plateNo ?? row.chassisNo].filter((p) => p !== null && p.length > 0).join(' · ');
}

/** The customer's service history: every job card, newest first (the list's own order). */
export function WorkshopTab({ customerUid }: { customerUid: string }): ReactNode {
  const { t } = useTranslation();
  const jobType = (s: string): string => t(`customers.history.jobType.${s}`, { defaultValue: s });

  return (
    <HistorySection<CustomerJobCard>
      list="job-cards"
      customerUid={customerUid}
      fetchPage={(cursor) => CUSTOMER_HISTORY['job-cards'](customerUid, cursor)}
      title={t('customers.history.jobCards.title')}
      empty={t('customers.history.jobCards.empty')}
      forbidden={t('customers.history.jobCards.forbidden')}
      href={(row) => `/workshop/job-cards/${row.uid}`}
      columns={[
        { title: t('customers.history.col.jobNo'), dataIndex: 'jobNo', width: 180 },
        { title: t('customers.history.col.bike'), key: 'bike', render: (_: unknown, row) => orDash(bike(row)) },
        { title: t('customers.history.col.type'), dataIndex: 'jobType', render: (v: string) => jobType(v) },
        {
          title: t('customers.history.col.status'),
          key: 'status',
          render: (_: unknown, row) => (
            <Space size={4} wrap>
              <Tag>{row.statusLabel}</Tag>
              {row.isComeback && <Tag color="warning">{t('customers.history.comeback')}</Tag>}
            </Space>
          ),
        },
        { title: t('customers.history.col.opened'), dataIndex: 'arrivedAt', width: 130, render: (v: string) => formatDate(v) },
        {
          title: t('customers.history.col.closed'),
          dataIndex: 'closedAt',
          width: 130,
          render: (v: string | null) => formatDate(v),
        },
      ]}
      renderMobileRow={(row) => (
        <HistoryCard
          number={row.jobNo}
          status={row.statusLabel}
          {...(row.isComeback ? { flag: t('customers.history.comeback') } : {})}
          lines={[bike(row), `${jobType(row.jobType)} · ${formatDate(row.arrivedAt)}`]}
        />
      )}
    />
  );
}
